<template>
  <div class="payments-page">
    <div class="page-header">
      <h1>Учет оплат</h1>
      <div class="header-actions">
        <div class="debt-badge" v-if="debtSummaryLoaded">
          <span class="label">Фактическая задолженность:</span>
          <span class="value">{{ formatCurrency(totalActualDebt) }}</span>
        </div>
        <Button 
          label="Неоплаченные заказы" 
          icon="pi pi-exclamation-triangle" 
          @click="showUnpaidOrdersModal" 
          :loading="unpaidOrdersLoading"
          class="p-button-warning"
          :badge="unpaidOrdersCount > 0 ? unpaidOrdersCount.toString() : ''"
          badgeClass="p-badge-danger"
        />
        <Button 
          label="Синхронизировать статусы" 
          icon="pi pi-sync" 
          @click="syncStatuses" 
          :loading="syncStatusesLoading" 
          class="p-button-outlined" 
          severity="info"
        />
        <div class="sync-button-wrapper">
          <Button 
            label="Полная синхронизация" 
            icon="pi pi-refresh" 
            @click="syncOrders" 
            :loading="syncLoading" 
          />
          <i 
            class="pi pi-info-circle sync-info-icon" 
            v-tooltip.top="'Полная синхронизация автоматически выполняется каждый день в 9:00 по МСК. Нажмите кнопку, если нужно обновить данные сейчас.'"
          ></i>
        </div>
      </div>
    </div>

    <!-- Фильтры -->
    <div class="filters-section">
      <div class="p-field">
        <label for="customerFilter">Клиент</label>
        <InputText id="customerFilter" v-model="filters.customer" placeholder="Поиск по клиенту" />
      </div>
      <div class="p-field">
        <label for="statusFilter">Статус</label>
        <Dropdown 
          id="statusFilter" 
          v-model="filters.status" 
          :options="statusOptions" 
          optionLabel="label" 
          optionValue="value"
          placeholder="Выберите статус"
          :showClear="true"
        />
      </div>
      <div class="p-field">
        <label for="dateFrom">Дата от</label>
        <Calendar id="dateFrom" v-model="filters.dateFrom" dateFormat="dd.mm.yy" placeholder="Выберите дату" />
      </div>
      <div class="p-field">
        <label for="dateTo">Дата до</label>
        <Calendar id="dateTo" v-model="filters.dateTo" dateFormat="dd.mm.yy" placeholder="Выберите дату" />
      </div>
      <Button label="Применить фильтры" @click="loadOrders" />
      <Button label="Экспорт Excel" icon="pi pi-download" @click="exportToExcel" class="p-button-secondary" />
      <Button label="Отчёты" icon="pi pi-chart-line" @click="showReportsDialog" class="p-button-outlined p-button-secondary" />
    </div>

    <!-- Таблица заказов -->
    <DataTable
      :value="orders"
      :loading="loading"
      paginator
      :rows="10"
      :rowsPerPageOptions="[5, 10, 25]"
      tableStyle="min-width: 50rem"
      @row-select="onRowSelect"
      selectionMode="single"
      dataKey="id"
      v-model:selection="selectedOrder"
      :sortField="'order_number'"
      :sortOrder="-1"
    >
      <template #header>
        <div class="table-header">
          Заказы клиентов
        </div>
      </template>

      <Column field="order_number" header="Номер заказа" sortable>
        <template #body="slotProps">
          {{ slotProps.data.order_number }}
        </template>
      </Column>

      <Column field="order_date" header="Дата" sortable>
        <template #body="slotProps">
          {{ formatDate(slotProps.data.order_date) }}
        </template>
      </Column>

      <Column field="customer_name" header="Клиент" sortable />

      <Column field="total_amount" header="Сумма (заявл.)" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.total_amount) }}
        </template>
      </Column>

      <Column field="confirmed_total_amount" header="Сумма (подтв.)" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.confirmed_total_amount) }}
        </template>
      </Column>

      <Column field="advance_paid" header="Аванс" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.advance_paid) }}
        </template>
      </Column>

      <Column field="total_debt" header="Предп. задолженность" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.total_debt) }}
        </template>
      </Column>

      <Column field="actual_debt" header="Факт. задолженность" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.actual_debt) }}
        </template>
      </Column>

      <Column field="margin" header="Маржа" sortable>
        <template #body="slotProps">
          {{ formatCurrency(slotProps.data.margin) }}
        </template>
      </Column>

      <Column field="margin_percentage" header="Маржа %" sortable>
        <template #body="slotProps">
          {{ slotProps.data.margin_percentage ? slotProps.data.margin_percentage.toFixed(1) + '%' : '' }}
        </template>
      </Column>

      <Column field="completion_percentage" header="Процент выполнения" sortable>
        <template #body="slotProps">
          {{ slotProps.data.completion_percentage ? slotProps.data.completion_percentage.toFixed(1) + '%' : '0%' }}
        </template>
      </Column>

      <Column header="Действия">
        <template #body="slotProps">
          <Button
            icon="pi pi-eye"
            class="p-button-rounded p-button-text"
            @click="viewOrderDetails(slotProps.data)"
            v-tooltip.top="'Посмотреть детали заказа'"
          />
        </template>
      </Column>
    </DataTable>

    <!-- Диалог деталей заказа -->
    <Dialog v-model:visible="showOrderDialog" header="Детали заказа" :style="{ width: '70vw' }">
      <div v-if="selectedOrderDetails">
        <div class="order-summary">
          <h3>Заказ {{ selectedOrderDetails.order.order_number }}</h3>
          <p>Клиент: {{ selectedOrderDetails.order.customer_name }}</p>
          <p>Сумма (заявленная): {{ formatCurrency(selectedOrderDetails.order.total_amount) }}</p>
          <p>Сумма (подтверждённая): {{ formatCurrency(selectedOrderDetails.order.confirmed_total_amount) }}</p>
          <p>Аванс: {{ formatCurrency(selectedOrderDetails.order.advance_paid) }}</p>
          <p>Задолженность: {{ formatCurrency(selectedOrderDetails.order.total_debt) }}</p>
        </div>

        <DataTable :value="selectedOrderDetails.items" tableStyle="min-width: 50rem">
          <Column field="name" header="Название" />
          <Column field="source_oem" header="Артикул" />
          <Column field="sale_price" header="Цена" />
          <Column field="quantity" header="Заказано" />
          <Column field="qnt_confirmed" header="Подтверждено" />
          <Column field="qnt_accept" header="Принято" />
          <Column field="qnt_income" header="Поступило" />
          <Column field="status" header="Статус">
            <template #body="slotProps">
              <div v-if="editingItemId === slotProps.data.id" class="status-editor">
                <Dropdown
                  v-model="newStatus"
                  :options="availableStatuses"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Выберите статус"
                  class="status-dropdown"
                />
                <Button
                  icon="pi pi-check"
                  class="p-button-success p-button-sm"
                  @click="saveItemStatus"
                />
                <Button
                  icon="pi pi-times"
                  class="p-button-danger p-button-sm"
                  @click="cancelEditStatus"
                />
              </div>
              <div v-else class="status-display">
                {{ slotProps.data.status }}
                <Button
                  icon="pi pi-pencil"
                  class="p-button-text p-button-sm"
                  @click="editItemStatus(slotProps.data)"
                />
              </div>
            </template>
          </Column>
          <Column field="distributed_advance" header="Распределенный аванс">
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.distributed_advance) }}
            </template>
          </Column>
          <Column field="remaining_debt" header="Остаток">
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.remaining_debt) }}
            </template>
          </Column>
        </DataTable>

        <!-- Секция платежей -->
        <div class="payments-section" style="margin-top: 2rem;">
          <div class="payments-header">
            <h4>История платежей</h4>
            <Button 
              label="Добавить платеж" 
              icon="pi pi-plus" 
              class="p-button-sm" 
              @click="showAddPaymentDialog = true" 
            />
          </div>
          
          <DataTable 
            :value="orderPayments" 
            :loading="paymentsLoading"
            class="payments-table"
            size="small"
          >
            <Column field="payment_date" header="Дата">
              <template #body="slotProps">
                {{ formatDate(slotProps.data.payment_date) }}
              </template>
            </Column>
            <Column field="amount" header="Сумма">
              <template #body="slotProps">
                {{ formatCurrency(slotProps.data.amount) }}
              </template>
            </Column>
            <Column field="payment_type" header="Тип" />
            <Column field="method" header="Способ" />
            <Column field="comment" header="Комментарий" />
            <Column header="Действия">
              <template #body="slotProps">
                <Button
                  icon="pi pi-trash"
                  class="p-button-rounded p-button-text p-button-danger p-button-sm"
                  @click="deletePayment(slotProps.data.id)"
                />
              </template>
            </Column>
          </DataTable>
          
          <div class="payments-summary" style="margin-top: 1rem; text-align: right;">
            <strong>Общая сумма платежей: {{ formatCurrency(totalPaymentsAmount) }}</strong>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Диалог добавления платежа -->
    <Dialog v-model:visible="showAddPaymentDialog" header="Добавить платеж" :style="{ width: '40vw' }">
      <div class="p-field">
        <label for="paymentDate">Дата платежа</label>
        <Calendar id="paymentDate" v-model="newPayment.date" dateFormat="dd.mm.yy" />
      </div>
      <div class="p-field">
        <label for="paymentAmount">Сумма</label>
        <InputNumber
          id="paymentAmount"
          v-model="newPayment.amount"
          mode="currency"
          currency="RUB"
          locale="ru-RU"
        />
      </div>
      <div class="p-field">
        <label for="paymentType">Тип платежа</label>
        <Dropdown
          id="paymentType"
          v-model="newPayment.type"
          :options="paymentTypes"
          optionLabel="label"
          optionValue="value"
          placeholder="Выберите тип"
        />
      </div>
      <div class="p-field">
        <label for="paymentMethod">Способ оплаты</label>
        <InputText id="paymentMethod" v-model="newPayment.method" placeholder="Нал, безнал, карта..." />
      </div>
      <div class="p-field">
        <label for="paymentComment">Комментарий</label>
        <InputText id="paymentComment" v-model="newPayment.comment" />
      </div>
      <div class="p-field-checkbox">
        <input 
          type="checkbox" 
          id="payDeliveredItems" 
          v-model="newPayment.payDeliveredItems" 
          class="p-checkbox-input"
        />
        <label for="payDeliveredItems" class="p-checkbox-label">
          Оплатить доставленные товары (сумма будет рассчитана автоматически)
        </label>
      </div>
      <template #footer>
        <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="showAddPaymentDialog = false" />
        <Button label="Добавить" icon="pi pi-check" @click="addPayment" />
      </template>
    </Dialog>

    <!-- Модальное окно отчётов -->
    <Dialog 
      header="Отчёты" 
      v-model:visible="showReports" 
      :style="{ width: '90vw', maxWidth: '1200px' }" 
      :modal="true"
      :closable="true"
    >
      <div class="reports-content">
        <!-- Фильтры отчётов -->
        <div class="reports-filters">
          <h3>Параметры отчёта</h3>
          <div class="filters-row">
            <div class="p-field">
              <label for="reportType">Тип отчёта</label>
              <Dropdown 
                id="reportType" 
                v-model="reportFilters.type" 
                :options="reportTypes" 
                optionLabel="label" 
                optionValue="value"
                placeholder="Выберите тип отчёта"
              />
            </div>
            <div class="p-field">
              <label for="reportPeriod">Период</label>
              <Dropdown 
                id="reportPeriod" 
                v-model="reportFilters.period" 
                :options="reportPeriods" 
                optionLabel="label" 
                optionValue="value"
                placeholder="Выберите период"
                @change="updateDateRange"
              />
            </div>
            <div class="p-field" v-if="reportFilters.period === 'custom'">
              <label for="reportDateFrom">Дата от</label>
              <Calendar 
                id="reportDateFrom" 
                v-model="reportFilters.dateFrom" 
                dateFormat="dd.mm.yy" 
                placeholder="Выберите дату"
                @input="loadReportData"
              />
            </div>
            <div class="p-field" v-if="reportFilters.period === 'custom'">
              <label for="reportDateTo">Дата до</label>
              <Calendar 
                id="reportDateTo" 
                v-model="reportFilters.dateTo" 
                dateFormat="dd.mm.yy" 
                placeholder="Выберите дату"
                @input="loadReportData"
              />
            </div>
          </div>
          <div class="filters-actions">
            <Button label="Обновить отчёт" icon="pi pi-refresh" @click="loadReportData" :loading="reportLoading" />
            <Button label="Экспорт Excel" icon="pi pi-download" @click="exportReport" class="p-button-secondary" />
          </div>
        </div>

        <!-- Результаты отчётов -->
        <div class="reports-results" v-if="reportData.length > 0 || reportFilters.type === 'customer_summary'">
          <!-- Сводный отчёт по клиентам -->
          <div v-if="reportFilters.type === 'customer_summary'" class="report-section">
            <h3>Сводный отчёт по клиентам и оплатам</h3>
            <div v-if="reportLoading" class="loading-state">
              <i class="pi pi-spinner pi-spin"></i>
              <p>Генерация отчёта... Пожалуйста, подождите.</p>
              <p style="font-size: 0.9rem; color: #666;">Отчёт будет автоматически скачан после завершения генерации.</p>
            </div>
            <div v-else class="empty-message">
              <i class="pi pi-file-excel" style="font-size: 3rem; color: #4CAF50;"></i>
              <p><strong>Выберите период и нажмите "Обновить отчёт"</strong></p>
              <p style="margin-top: 1rem;">Отчёт генерируется в формате Excel и содержит:</p>
              <ul style="text-align: left; display: inline-block; margin-top: 1rem;">
                <li>Сводка по всем заказам с детализацией</li>
                <li>Группировка по клиентам</li>
                <li>Информация о заказанных суммах</li>
                <li>Данные об оплатах</li>
                <li>Позиции в пути</li>
                <li>Фактическая и прогнозируемая задолженность</li>
              </ul>
              <p style="margin-top: 1rem; font-size: 0.9rem; color: #666;">
                💡 Совет: Выберите "Все заказы" для получения полного отчёта без фильтрации по датам,<br/>
                или "Текущий год" для отчёта за {{ new Date().getFullYear() }} год
              </p>
            </div>
          </div>

          <!-- Отчёт по маржинальности -->
          <div v-if="reportFilters.type === 'margin'" class="report-section">
            <h3>Отчёт по маржинальности за период</h3>
            <DataTable :value="reportData" :loading="reportLoading" responsiveLayout="scroll">
              <Column field="period" header="Период" sortable />
              <Column field="revenue" header="Выручка" sortable>
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.revenue) }}
                </template>
              </Column>
              <Column field="cost" header="Себестоимость" sortable>
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.cost) }}
                </template>
              </Column>
              <Column field="margin" header="Прибыль" sortable>
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.margin) }}
                </template>
              </Column>
              <Column field="margin_percentage" header="Маржинальность %" sortable>
                <template #body="slotProps">
                  <span :class="getMarginClass(slotProps.data.margin_percentage)">
                    {{ slotProps.data.margin_percentage.toFixed(1) }}%
                  </span>
                </template>
              </Column>
              <Column field="items_count" header="Кол-во позиций" sortable />
            </DataTable>
          </div>

          <!-- Отчёт по наценке -->
          <div v-if="reportFilters.type === 'markup'" class="report-section">
            <h3>Отчёт по наценке по клиентам</h3>
            <DataTable :value="reportData" :loading="reportLoading" responsiveLayout="scroll">
              <Column field="group_name" header="Клиент" sortable />
              <Column field="sale_amount" header="Выручка" sortable>
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.sale_amount) }}
                </template>
              </Column>
              <Column field="cost_amount" header="Себестоимость" sortable>
                <template #body="slotProps">
                  {{ formatCurrency(slotProps.data.cost_amount) }}
                </template>
              </Column>
              <Column field="markup_percentage" header="Наценка %" sortable>
                <template #body="slotProps">
                  <span :class="getMarkupClass(slotProps.data.markup_percentage)">
                    {{ slotProps.data.markup_percentage.toFixed(1) }}%
                  </span>
                </template>
              </Column>
              <Column field="items_count" header="Кол-во позиций" sortable />
            </DataTable>
          </div>

          <!-- Итоговая статистика -->
          <div class="report-summary">
            <h4>Итого:</h4>
            <div class="summary-cards">
              <div class="summary-card">
                <span class="label">Общая выручка:</span>
                <span class="value">{{ formatCurrency(reportSummary.total_revenue) }}</span>
              </div>
              <div class="summary-card">
                <span class="label">Общая себестоимость:</span>
                <span class="value">{{ formatCurrency(reportSummary.total_cost) }}</span>
              </div>
              <div class="summary-card">
                <span class="label">Общая прибыль:</span>
                <span class="value">{{ formatCurrency(reportSummary.total_profit) }}</span>
              </div>
              <div class="summary-card" v-if="reportFilters.type === 'margin'">
                <span class="label">Средняя маржинальность:</span>
                <span class="value">{{ reportSummary.avg_margin?.toFixed(1) || 0 }}%</span>
              </div>
              <div class="summary-card" v-if="reportFilters.type === 'markup'">
                <span class="label">Средняя наценка:</span>
                <span class="value">{{ reportSummary.avg_markup?.toFixed(1) || 0 }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Пустое состояние -->
        <div v-else-if="!reportLoading && reportFilters.type !== 'customer_summary'" class="empty-state">
          <div class="empty-message">
            <i class="pi pi-chart-line" style="font-size: 3rem; color: #ccc;"></i>
            <p>Выберите тип отчёта и период для просмотра данных</p>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Модальное окно неоплаченных заказов -->
    <Dialog 
      v-model:visible="showUnpaidOrders" 
      modal 
      header="Неоплаченные заказы" 
      :style="{ width: '100vw', maxWidth: '1125px' }"
      :draggable="false"
    >
      <div v-if="unpaidOrdersLoading" class="loading-state">
        <i class="pi pi-spinner pi-spin"></i>
        <p>Загрузка неоплаченных заказов...</p>
      </div>

      <div v-else-if="unpaidOrders.length > 0">
        <!-- Сводка -->
        <div class="unpaid-summary">
          <div class="summary-card">
            <span class="label">Всего заказов:</span>
            <span class="value">{{ unpaidOrders.length }}</span>
          </div>
          <div class="summary-card">
            <span class="label">Общий долг:</span>
            <span class="value debt-amount">{{ formatCurrency(unpaidOrdersTotalDebt) }}</span>
          </div>
        </div>

        <!-- Таблица неоплаченных заказов -->
        <DataTable 
          :value="unpaidOrders" 
          :paginator="true" 
          :rows="10"
          tableStyle="margin-top: 1rem"
          stripedRows
        >
          <Column field="order_number" header="Номер заказа" sortable />
          <Column field="customer_name" header="Клиент" sortable />
          <Column field="delivered_items_count" header="Доставленных позиций" sortable />
          <Column field="total_delivered_amount" header="Сумма доставленного" sortable>
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.total_delivered_amount) }}
            </template>
          </Column>
          <Column field="advance_paid" header="Внесено авансом" sortable>
            <template #body="slotProps">
              {{ formatCurrency(slotProps.data.advance_paid) }}
            </template>
          </Column>
          <Column field="delivered_debt" header="Долг за доставленное" sortable>
            <template #body="slotProps">
              <span class="debt-amount">{{ formatCurrency(slotProps.data.delivered_debt) }}</span>
            </template>
          </Column>
          <Column header="Действия">
            <template #body="slotProps">
              <div class="action-buttons">
                <Button 
                  icon="pi pi-eye" 
                  @click="viewUnpaidOrderDetails(slotProps.data)" 
                  class="p-button-outlined p-button-sm"
                  v-tooltip="'Детали заказа'"
                />
                <Button 
                  icon="pi pi-download" 
                  @click="exportDeliveredItems(slotProps.data.order_id)" 
                  class="p-button-outlined p-button-success p-button-sm"
                  v-tooltip="'Распечатать (Excel)'"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>

      <div v-else class="empty-state">
        <i class="pi pi-check-circle" style="font-size: 3rem; color: #28a745;"></i>
        <p>Все доставленные заказы оплачены!</p>
      </div>
    </Dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Calendar from 'primevue/calendar'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import ScrollPanel from 'primevue/scrollpanel'
import Dropdown from 'primevue/dropdown'
import { useToast } from 'primevue/usetoast'
import apiService from '../services/apiService.js'

export default {
  name: 'PaymentsPage',
  components: {
    DataTable,
    Column,
    Button,
    InputText,
    Calendar,
    Dialog,
    InputNumber,
    ScrollPanel,
    Dropdown
  },
  setup() {
    const toast = useToast()

    const orders = ref([])
    const loading = ref(false)
    const syncLoading = ref(false)
    const syncStatusesLoading = ref(false)
    const totalActualDebt = ref(0)
    const debtSummaryLoaded = ref(false)
    const selectedOrder = ref(null)
    const showOrderDialog = ref(false)
    const selectedOrderDetails = ref(null)
    const availableStatuses = ref([])
    const editingItemId = ref(null)
    const newStatus = ref('')

    // Платежи
    const orderPayments = ref([])
    const paymentsLoading = ref(false)
    const totalPaymentsAmount = ref(0)
    const showAddPaymentDialog = ref(false)
    const newPayment = ref({
      date: new Date(),
      amount: 0,
      type: 'advance',
      method: '',
      comment: '',
      payDeliveredItems: false
    })
    
    const paymentTypes = ref([
      { label: 'Аванс', value: 'advance' },
      { label: 'Доплата', value: 'final' },
      { label: 'Возврат', value: 'refund' }
    ])

    const statusOptions = ref([
      { label: 'Все статусы', value: null },
      { label: 'Заморожено', value: 'Заморожено' },
      { label: 'В работе', value: 'В работе' },
      { label: 'Выдано', value: 'Выдано' },
      { label: 'Доставлено', value: 'Доставлено' },
      { label: 'Оплачено', value: 'Оплачено' },
      { label: 'Оплачен', value: 'Оплачен' },
      { label: 'Отменено', value: 'Отменено' }
    ])

    const filters = ref({
      customer: '',
      status: null,
      dateFrom: null,
      dateTo: null
    })

    // Отчёты
    const showReports = ref(false)
    const reportLoading = ref(false)

    // Неоплаченные заказы
    const unpaidOrdersLoading = ref(false)
    const unpaidOrdersCount = ref(0)
    const showUnpaidOrders = ref(false)
    const unpaidOrders = ref([])
    const unpaidOrdersTotalDebt = ref(0)
    const reportData = ref([])
    const reportSummary = ref({
      total_revenue: 0,
      total_cost: 0,
      total_profit: 0,
      avg_margin: 0,
      avg_markup: 0
    })
    
    const reportFilters = ref({
      type: 'margin',
      period: 'month',
      dateFrom: null,
      dateTo: null
    })
    
    const reportTypes = ref([
      { label: 'Отчёт по маржинальности', value: 'margin' },
      { label: 'Отчёт по наценке', value: 'markup' },
      { label: 'Сводный отчёт по клиентам', value: 'customer_summary' }
    ])
    
    const reportPeriods = ref([
      { label: 'Все заказы', value: 'all' },
      { label: 'Текущий год', value: 'year' },
      { label: 'Текущий квартал', value: 'quarter' },
      { label: 'Текущий месяц', value: 'month' },
      { label: 'Прошлый месяц', value: 'prev_month' },
      { label: 'Произвольный период', value: 'custom' }
    ])

    const loadOrders = async () => {
      loading.value = true
      try {
        const params = {}
        if (filters.value.customer) params.customer = filters.value.customer
        if (filters.value.status) params.status = filters.value.status
        if (filters.value.dateFrom) params.date_from = filters.value.dateFrom.toISOString().split('T')[0]
        if (filters.value.dateTo) params.date_to = filters.value.dateTo.toISOString().split('T')[0]

        const response = await apiService.get('/payments/orders', { params })
        orders.value = response.data
        await loadDebtSummary()
      } catch (error) {
        // Если неавторизованы, глобальный перехватчик сделает редирект сам — тост не показываем
        const status = error?.response?.status
        if (status !== 401 && status !== 403) {
          toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить заказы', life: 3000 })
        }
      } finally {
        loading.value = false
      }
    }

    const loadDebtSummary = async () => {
      try {
        const response = await apiService.get('/payments/debt-summary')
        totalActualDebt.value = response.data.total_actual_debt || 0
        debtSummaryLoaded.value = true
      } catch (e) {
        debtSummaryLoaded.value = false
      }
    }

    const loadStatuses = async () => {
      try {
        const response = await apiService.get('/payments/statuses')
        availableStatuses.value = response.data.statuses.map(status => ({ label: status, value: status }))
      } catch (error) {
        // Используем стандартные статусы как fallback
        availableStatuses.value = [
          { label: 'Заморожено', value: 'Заморожено' },
          { label: 'В работе', value: 'В работе' },
          { label: 'Выдано', value: 'Выдано' },
          { label: 'Оплачен', value: 'Оплачен' },
          { label: 'Отменено', value: 'Отменено' }
        ]
      }
    }

    const syncOrders = async () => {
      syncLoading.value = true
      try {
        const params = {}
        if (filters.value.dateFrom) params.date_from = filters.value.dateFrom.toISOString().split('T')[0]
        if (filters.value.dateTo) params.date_to = filters.value.dateTo.toISOString().split('T')[0]
        
        const response = await apiService.post('/payments/sync-orders', params)
        toast.add({ 
          severity: 'success', 
          summary: 'Успех', 
          detail: `Полная синхронизация: ${response.data.result.orders} заказов и ${response.data.result.items} позиций`, 
          life: 5000 
        })
        await loadOrders()
        await loadDebtSummary()
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Ошибка полной синхронизации'
        toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
      } finally {
        syncLoading.value = false
      }
    }

    const syncStatuses = async () => {
      syncStatusesLoading.value = true
      try {
        const params = {}
        if (filters.value.dateFrom) params.date_from = filters.value.dateFrom.toISOString().split('T')[0]
        if (filters.value.dateTo) params.date_to = filters.value.dateTo.toISOString().split('T')[0]
        
        const response = await apiService.post('/payments/sync-statuses', params)
        toast.add({ 
          severity: 'success', 
          summary: 'Успех', 
          detail: `Синхронизация статусов: обработано ${response.data.result.synced_items} позиций, обновлено ${response.data.result.updated_statuses} статусов`, 
          life: 5000 
        })
        await loadOrders()
        await loadDebtSummary()
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Ошибка синхронизации статусов'
        toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
      } finally {
        syncStatusesLoading.value = false
      }
    }

    const viewOrderDetails = async (order) => {
      try {
        const response = await apiService.get(`/payments/orders/${order.id}`)
        selectedOrderDetails.value = response.data
        await loadStatuses()
        await loadOrderPayments(order.id)
        showOrderDialog.value = true
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить детали заказа', life: 3000 })
      }
    }

    const editItemStatus = (item) => {
      editingItemId.value = item.id
      newStatus.value = item.status
    }

    const saveItemStatus = async () => {
      try {
        await apiService.put(`/payments/orders/items/${editingItemId.value}/status`, {
          status: newStatus.value
        })
        toast.add({ severity: 'success', summary: 'Успех', detail: 'Статус обновлен', life: 3000 })
        editingItemId.value = null
        newStatus.value = ''
        // Перезагрузить детали заказа
        if (selectedOrderDetails.value) {
          const response = await apiService.get(`/payments/orders/${selectedOrderDetails.value.order.id}`)
          selectedOrderDetails.value = response.data
        }
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Ошибка обновления статуса', life: 3000 })
      }
    }

    const cancelEditStatus = () => {
      editingItemId.value = null
      newStatus.value = ''
    }

    const exportToExcel = async () => {
      try {
        const params = {}
        if (filters.value.customer) params.customer = filters.value.customer
        if (filters.value.status) params.status = filters.value.status
        if (filters.value.dateFrom) params.date_from = filters.value.dateFrom.toISOString().split('T')[0]
        if (filters.value.dateTo) params.date_to = filters.value.dateTo.toISOString().split('T')[0]

        const response = await apiService.get('/payments/export', { params, responseType: 'blob' })
        
        // Создаем ссылку для скачивания
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `orders_${new Date().toISOString().split('T')[0]}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        toast.add({ severity: 'success', summary: 'Успех', detail: 'Отчёт экспортирован', life: 3000 })
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Ошибка экспорта', life: 3000 })
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      return new Date(dateString).toLocaleDateString('ru-RU')
    }

    const formatCurrency = (value) => {
      if (value === null || value === undefined || value === 0) return '0,00 ₽'
      return new Intl.NumberFormat('ru-RU', { 
        style: 'currency', 
        currency: 'RUB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    }

    onMounted(() => {
      loadOrders()
      loadDebtSummary()
      loadUnpaidOrdersCount()
    })

    // Функции для платежей
    const loadOrderPayments = async (orderId) => {
      paymentsLoading.value = true
      try {
        const response = await apiService.get(`/payments/orders/${orderId}/payments`)
        orderPayments.value = response.data.payments
        totalPaymentsAmount.value = response.data.total_amount
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить платежи', life: 3000 })
      } finally {
        paymentsLoading.value = false
      }
    }

    const addPayment = async () => {
      try {
        const paymentData = {
          payment_date: newPayment.value.date.toISOString().split('T')[0],
          amount: newPayment.value.amount,
          payment_type: newPayment.value.type,
          method: newPayment.value.method || null,
          comment: newPayment.value.comment || null,
          pay_delivered_items: newPayment.value.payDeliveredItems || false
        }
        
        const response = await apiService.post(`/payments/orders/${selectedOrderDetails.value.order.id}/payments`, paymentData)
        
        let successMessage = 'Платеж добавлен'
        if (response.data.delivered_items_paid > 0) {
          successMessage += `. Оплачено ${response.data.delivered_items_paid} доставленных позиций`
        }
        
        toast.add({ severity: 'success', summary: 'Успех', detail: successMessage, life: 5000 })
        showAddPaymentDialog.value = false
        
        // Сброс формы
        newPayment.value = {
          date: new Date(),
          amount: 0,
          type: 'advance',
          method: '',
          comment: '',
          payDeliveredItems: false
        }
        
        // Перезагрузка данных
        await loadOrderPayments(selectedOrderDetails.value.order.id)
        await loadOrders() // обновляем общий список для пересчёта авансов
        await loadUnpaidOrdersCount() // обновляем счетчик неоплаченных заказов
        
        // Если были изменены статусы, обновляем детали заказа
        if (response.data.delivered_items_paid > 0) {
          await viewOrderDetails(selectedOrderDetails.value.order)
        }
        
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Не удалось добавить платеж'
        toast.add({ severity: 'error', summary: 'Ошибка', detail: errorMessage, life: 5000 })
      }
    }

    const deletePayment = async (paymentId) => {
      try {
        await apiService.delete(`/payments/orders/${selectedOrderDetails.value.order.id}/payments/${paymentId}`)
        toast.add({ severity: 'success', summary: 'Успех', detail: 'Платеж удален', life: 3000 })
        
        // Перезагрузка данных
        await loadOrderPayments(selectedOrderDetails.value.order.id)
        await loadOrders()
        
      } catch (error) {
        toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось удалить платеж', life: 3000 })
      }
    }

    // Методы для отчётов
    const showReportsDialog = () => {
      showReports.value = true
      // Устанавливаем период по умолчанию
      if (!reportFilters.value.period) {
        reportFilters.value.period = 'all'  // По умолчанию "Все заказы"
      }
      // Устанавливаем даты для не-custom и не-all периодов
      if (reportFilters.value.period !== 'custom' && reportFilters.value.period !== 'all') {
        updateDateRange()
      }
      // НЕ запускаем автоматическую генерацию - пользователь должен нажать кнопку
    }

    const updateDateRange = () => {
      const now = new Date()
      if (reportFilters.value.period === 'all') {
        // Для "Все заказы" не устанавливаем даты - будут выбраны все заказы
        reportFilters.value.dateFrom = null
        reportFilters.value.dateTo = null
        // Не запускаем автоматически для customer_summary
        if (reportFilters.value.type !== 'customer_summary') {
          loadReportData()
        }
      } else if (reportFilters.value.period === 'month') {
        // Текущий месяц: с 1 числа до последнего числа месяца (независимо от текущей даты)
        reportFilters.value.dateFrom = new Date(now.getFullYear(), now.getMonth(), 1)
        reportFilters.value.dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        // Не запускаем автоматически для customer_summary
        if (reportFilters.value.type !== 'customer_summary') {
          loadReportData()
        }
      } else if (reportFilters.value.period === 'prev_month') {
        // Прошлый месяц: с 1 числа до последнего числа прошлого месяца
        reportFilters.value.dateFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        reportFilters.value.dateTo = new Date(now.getFullYear(), now.getMonth(), 0)
        // Не запускаем автоматически для customer_summary
        if (reportFilters.value.type !== 'customer_summary') {
          loadReportData()
        }
      } else if (reportFilters.value.period === 'quarter') {
        // Квартал по временам года:
        // Зима: декабрь, январь, февраль (12, 1, 2)
        // Весна: март, апрель, май (3, 4, 5)
        // Лето: июнь, июль, август (6, 7, 8)
        // Осень: сентябрь, октябрь, ноябрь (9, 10, 11)
        const month = now.getMonth() + 1 // 1-12
        let startMonth, endMonth, startYear, endYear
        
        if (month >= 9 && month <= 11) {
          // Осень: сентябрь-ноябрь
          startMonth = 9
          endMonth = 11
          startYear = endYear = now.getFullYear()
        } else if (month >= 6 && month <= 8) {
          // Лето: июнь-август
          startMonth = 6
          endMonth = 8
          startYear = endYear = now.getFullYear()
        } else if (month >= 3 && month <= 5) {
          // Весна: март-май
          startMonth = 3
          endMonth = 5
          startYear = endYear = now.getFullYear()
        } else {
          // Зима: декабрь прошлого года - февраль текущего года
          if (month === 12) {
            startMonth = 12
            endMonth = 2
            startYear = now.getFullYear()
            endYear = now.getFullYear() + 1
          } else {
            // Январь или февраль
            startMonth = 12
            endMonth = 2
            startYear = now.getFullYear() - 1
            endYear = now.getFullYear()
          }
        }
        
        reportFilters.value.dateFrom = new Date(startYear, startMonth - 1, 1)
        reportFilters.value.dateTo = new Date(endYear, endMonth, 0)
        // Не запускаем автоматически для customer_summary
        if (reportFilters.value.type !== 'customer_summary') {
          loadReportData()
        }
      } else if (reportFilters.value.period === 'year') {
        // Текущий год: с 1 января по 31 декабря
        reportFilters.value.dateFrom = new Date(now.getFullYear(), 0, 1)
        reportFilters.value.dateTo = new Date(now.getFullYear(), 11, 31)
        // Не запускаем автоматически для customer_summary
        if (reportFilters.value.type !== 'customer_summary') {
          loadReportData()
        }
      } else if (reportFilters.value.period === 'custom') {
        reportFilters.value.dateFrom = null
        reportFilters.value.dateTo = null
        reportData.value = []
      }
    }

    const loadReportData = async () => {
      if (!reportFilters.value.type) return
      if (reportFilters.value.period === 'custom' && (!reportFilters.value.dateFrom || !reportFilters.value.dateTo)) return

      reportLoading.value = true
      try {
        const params = {
          date_from: reportFilters.value.dateFrom ? reportFilters.value.dateFrom.toISOString().split('T')[0] : null,
          date_to: reportFilters.value.dateTo ? reportFilters.value.dateTo.toISOString().split('T')[0] : null
        }

        // Для сводного отчёта по клиентам используем новый API
        if (reportFilters.value.type === 'customer_summary') {
          await generateCustomerSummaryReport()
          return
        }

        const endpoint = reportFilters.value.type === 'margin' 
          ? '/payments/reports/margin' 
          : '/payments/reports/markup'
        
        const response = await apiService.get(endpoint, { params })
        reportData.value = response.data.items || []
        
        // Вычисляем итоговую статистику  
        const totalRevenue = response.data.total_revenue || reportData.value.reduce((sum, item) => sum + (item.revenue || item.sale_amount || 0), 0)
        const totalCost = response.data.total_cost || reportData.value.reduce((sum, item) => sum + (item.cost || item.cost_amount || 0), 0)
        const totalProfit = totalRevenue - totalCost
        
        reportSummary.value = {
          total_revenue: totalRevenue,
          total_cost: totalCost,
          total_profit: totalProfit,
          avg_margin: totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0,
          avg_markup: totalCost > 0 ? ((totalRevenue - totalCost) / totalCost * 100) : 0
        }

      } catch (error) {
        toast.add({ 
          severity: 'error', 
          summary: 'Ошибка', 
          detail: 'Не удалось загрузить данные отчёта', 
          life: 3000 
        })
      } finally {
        reportLoading.value = false
      }
    }

    const generateCustomerSummaryReport = async () => {
      // Если выбран произвольный период, но даты не указаны - показываем предупреждение
      if (reportFilters.value.period === 'custom' && (!reportFilters.value.dateFrom || !reportFilters.value.dateTo)) {
        toast.add({ 
          severity: 'warn', 
          summary: 'Внимание', 
          detail: 'Укажите даты начала и окончания периода или выберите готовый период', 
          life: 4000 
        })
        return
      }

      // Если период не custom, но даты не установлены - устанавливаем их
      if (reportFilters.value.period !== 'custom' && (!reportFilters.value.dateFrom || !reportFilters.value.dateTo)) {
        updateDateRange()
        // Даём время на обновление
        await new Promise(resolve => setTimeout(resolve, 100))
      }

      reportLoading.value = true
      try {
        const params = new URLSearchParams()
        if (reportFilters.value.dateFrom) {
          params.append('date_from', reportFilters.value.dateFrom.toISOString().split('T')[0])
        }
        if (reportFilters.value.dateTo) {
          params.append('date_to', reportFilters.value.dateTo.toISOString().split('T')[0])
        }

        toast.add({ 
          severity: 'info', 
          summary: 'Генерация отчёта', 
          detail: 'Отчёт генерируется. Это может занять несколько секунд...', 
          life: 3000 
        })

        // Прямой запрос на генерацию с получением файла
        const response = await apiService.post(
          '/payments/reports/customer-summary?' + params.toString(),
          {},
          { responseType: 'blob' }
        )
        
        // Создаем ссылку для скачивания
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        
        // Извлекаем имя файла из заголовков или генерируем
        const contentDisposition = response.headers['content-disposition']
        let filename = 'customer_summary.xlsx'
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '')
          }
        }
        
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        toast.add({ 
          severity: 'success', 
          summary: 'Готово', 
          detail: 'Отчёт успешно сгенерирован и скачан', 
          life: 3000 
        })
        
      } catch (error) {
        console.error('Error generating customer summary:', error)
        toast.add({ 
          severity: 'error', 
          summary: 'Ошибка', 
          detail: error.response?.data?.detail || 'Не удалось сгенерировать отчёт', 
          life: 5000 
        })
      } finally {
        reportLoading.value = false
      }
    }

    const exportReport = async () => {
      if (!reportData.value.length) {
        toast.add({ 
          severity: 'warn', 
          summary: 'Предупреждение', 
          detail: 'Нет данных для экспорта', 
          life: 3000 
        })
        return
      }

      // Убеждаемся что даты установлены для не-custom периодов
      if (reportFilters.value.period !== 'custom' && (!reportFilters.value.dateFrom || !reportFilters.value.dateTo)) {
        updateDateRange()
      }

      try {
        const params = {
          type: reportFilters.value.type,
          date_from: reportFilters.value.dateFrom ? reportFilters.value.dateFrom.toISOString().split('T')[0] : null,
          date_to: reportFilters.value.dateTo ? reportFilters.value.dateTo.toISOString().split('T')[0] : null
        }

        const response = await apiService.get('/payments/reports/export', { 
          params,
          responseType: 'blob'
        })
        
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `report_${reportFilters.value.type}_${new Date().toISOString().split('T')[0]}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)

        toast.add({ 
          severity: 'success', 
          summary: 'Успех', 
          detail: 'Отчёт экспортирован', 
          life: 3000 
        })

      } catch (error) {
        toast.add({ 
          severity: 'error', 
          summary: 'Ошибка', 
          detail: 'Не удалось экспортировать отчёт', 
          life: 3000 
        })
      }
    }

    const getMarginClass = (margin) => {
      if (margin >= 30) return 'margin-high'
      if (margin >= 15) return 'margin-medium'
      return 'margin-low'
    }

    const getMarkupClass = (markup) => {
      if (markup >= 50) return 'markup-high'
      if (markup >= 25) return 'markup-medium'
      return 'markup-low'
    }

    // Функции для неоплаченных заказов
    const loadUnpaidOrdersCount = async () => {
      try {
        const response = await apiService.get('/payments/unpaid-orders')
        unpaidOrdersCount.value = response.data.total_count
        unpaidOrdersTotalDebt.value = response.data.total_debt
      } catch (error) {
        console.error('Ошибка загрузки количества неоплаченных заказов:', error)
      }
    }

    const loadUnpaidOrders = async () => {
      unpaidOrdersLoading.value = true
      try {
        const response = await apiService.get('/payments/unpaid-orders')
        unpaidOrders.value = response.data.orders
        unpaidOrdersCount.value = response.data.total_count
        unpaidOrdersTotalDebt.value = response.data.total_debt
      } catch (error) {
        toast.add({ 
          severity: 'error', 
          summary: 'Ошибка', 
          detail: 'Не удалось загрузить неоплаченные заказы', 
          life: 3000 
        })
      } finally {
        unpaidOrdersLoading.value = false
      }
    }

    const showUnpaidOrdersModal = async () => {
      await loadUnpaidOrders()
      showUnpaidOrders.value = true
    }

    const exportDeliveredItems = async (orderId) => {
      try {
        const response = await apiService.get(`/payments/orders/${orderId}/delivered-items-export`, {
          responseType: 'blob'
        })
        
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `delivered_items_order_${orderId}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        toast.add({ 
          severity: 'success', 
          summary: 'Успех', 
          detail: 'Файл экспортирован', 
          life: 3000 
        })
      } catch (error) {
        toast.add({ 
          severity: 'error', 
          summary: 'Ошибка', 
          detail: 'Не удалось экспортировать файл', 
          life: 3000 
        })
      }
    }

    const viewUnpaidOrderDetails = (order) => {
      // Переходим к деталям заказа, закрывая модальное окно неоплаченных заказов
      showUnpaidOrders.value = false
      // Адаптируем объект для метода viewOrderDetails
      const orderForDetails = {
        id: order.order_id,
        order_number: order.order_number,
        customer_name: order.customer_name
      }
      viewOrderDetails(orderForDetails)
    }

    return {
      orders,
      loading,
      syncLoading,
      syncStatusesLoading,
      totalActualDebt,
      debtSummaryLoaded,
      selectedOrder,
      showOrderDialog,
      selectedOrderDetails,
      filters,
      statusOptions,
      availableStatuses,
      editingItemId,
      newStatus,
      // Платежи
      orderPayments,
      paymentsLoading,
      totalPaymentsAmount,
      showAddPaymentDialog,
      newPayment,
      paymentTypes,
      loadOrders,
      loadStatuses,
      syncOrders,
      syncStatuses,
      viewOrderDetails,
      editItemStatus,
      saveItemStatus,
      cancelEditStatus,
      exportToExcel,
      formatDate,
      formatCurrency,
      loadDebtSummary,
      loadOrderPayments,
      addPayment,
      deletePayment,
      // Отчёты
      showReports,
      reportLoading,
      reportData,
      reportSummary,
      reportFilters,
      reportTypes,
      reportPeriods,
      showReportsDialog,
      updateDateRange,
      loadReportData,
      generateCustomerSummaryReport,
      exportReport,
      getMarginClass,
      getMarkupClass,
      // Неоплаченные заказы
      unpaidOrdersLoading,
      unpaidOrdersCount,
      showUnpaidOrders,
      unpaidOrders,
      unpaidOrdersTotalDebt,
      showUnpaidOrdersModal,
      loadUnpaidOrders,
      exportDeliveredItems,
      viewUnpaidOrderDetails
    }
  }
}
</script>

<style scoped>
.payments-page {
  padding: 1rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.sync-button-wrapper {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  position: relative;
}

.sync-info-icon {
  font-size: 1.1rem;
  color: #6c757d;
  cursor: help;
  transition: color 0.2s;
}

.sync-info-icon:hover {
  color: #0b74de;
}

.debt-badge {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 8px;
  background: #f0f7ff;
  color: #0b74de;
  font-weight: 600;
}
.debt-badge .label { opacity: 0.8; }

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: end;
}

.p-field {
  display: flex;
  flex-direction: column;
}

.table-header {
  font-weight: bold;
  font-size: 1.2rem;
}

.order-summary {
  margin-bottom: 1rem;
}

.order-summary h3 {
  margin: 0 0 0.5rem 0;
}

.order-summary p {
  margin: 0.25rem 0;
}

.status-editor {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.payments-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 1rem;
}

.payments-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.payments-header h4 {
  margin: 0;
}

.payments-table {
  margin-bottom: 1rem;
}

.payments-summary {
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.status-dropdown {
  min-width: 120px;
}

/* Стили для отчётов */
.reports-content {
  padding: 1rem;
}

.reports-filters {
  margin-bottom: 2rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #f8f9fa;
}

.reports-filters h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #495057;
}

.filters-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.filters-actions {
  display: flex;
  gap: 0.5rem;
}

.report-section {
  margin-bottom: 2rem;
}

.report-section h3 {
  margin-bottom: 1rem;
  color: #495057;
}

.report-summary {
  margin-top: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.summary-cards {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  min-width: 150px;
}

.summary-card .label {
  font-size: 0.85rem;
  color: #6c757d;
}

.summary-card .value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
}

.empty-message {
  color: #6c757d;
}

.empty-message i {
  display: block;
  margin-bottom: 1rem;
}

/* Классы для индикации значений маржинальности */
.margin-high {
  color: #28a745;
  font-weight: 600;
}

.margin-medium {
  color: #ffc107;
  font-weight: 600;
}

.margin-low {
  color: #dc3545;
  font-weight: 600;
}

/* Классы для индикации значений наценки */
.markup-high {
  color: #28a745;
  font-weight: 600;
}

.markup-medium {
  color: #ffc107;
  font-weight: 600;
}

.markup-low {
  color: #dc3545;
  font-weight: 600;
}

/* Стили для неоплаченных заказов */
.unpaid-summary {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.debt-amount {
  color: #dc3545;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.loading-state {
  text-align: center;
  padding: 2rem;
}

.loading-state i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #6c757d;
}

.loading-state p {
  color: #6c757d;
  margin: 0;
}

/* Стили для чекбокса */
.p-field-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.p-checkbox-input {
  width: 16px;
  height: 16px;
  accent-color: #007bff;
}

.p-checkbox-label {
  font-size: 0.9rem;
  color: #495057;
  cursor: pointer;
  margin: 0;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }
  .header-actions {
    flex-wrap: wrap;
  }
  .filters-section {
    flex-direction: column;
  }
  .debt-badge {
    font-size: 0.8rem;
  }
}
</style>
